<template>
  <transition
    :name="transition"
    v-on="transitionEvent"
    @after-enter="$emit('after-enter')"
    @after-leave="$emit('after-leave')">
    <div
      v-if="!lazyRender || rendered"
      v-show="rendered && localVisible"
      ref="popper"
      v-outside="outsideOptions"
      :style="realPopperStyle"
      :class="popperClass"
      :x-has-arrow="visibleArrow"
      :x-loading="loading">
      <template v-if="loading">
        <loading size="40px"></loading>
      </template>
      <div v-show="!loading">
        <slot></slot>
      </div>
      <div
        v-if="visibleArrow"
        key="arrow"
        ref="arrow"
        :class="arrowClass"></div>
    </div>
  </transition>
</template>

<script>
import Popper from "popper.js/dist/umd/popper"
import { getScrollParent } from "popper.js/dist/umd/popper-utils"
import { addListener, removeListener } from "resize-detector/dist"
import merge from "lodash/merge"
import isFunction from "lodash/isFunction"
import { getNodes } from "../../utils/context"
import outside from "../../directives/outside"
import Loading from "../loading"
import config from "../../managers/config"

config.defaults({
  "Popper.transition": "zoom-in-top",
  "Popper.placement": "bottom-start",
  "Popper.visibleArrow": false,
  "Popper.trigger": "click",
  "Popper.offset": 0,
  "Popper.delay": 300,
  "Popper.lazyRender": true,
  "Popper.appendToBody": true,
  "Popper.removeOnDestroy": true,
  "Popper.trackRefWidth": false,
})

const EVENTS = ["hover", "click", "mousedown", "mouseup"]
const transformTrigger = trigger => (Array.isArray(trigger) ? trigger : trigger.split(" "))

export default {
  name: "mm-popper",

  placements: Popper.placements,

  components: {
    Loading,
  },
  directives: { outside },

  inheritAttrs: false,
  provide() {
    return {
      "mm-popper": this,
    }
  },
  inject: {
    popperParent: {
      from: "mm-popper",
      default: () => null,
    },
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    transition: {
      type: String,
      default: () => config.get("Popper.transition"),
    },
    target: {
      type: null,
      required: true,
    },
    placement: {
      type: String,
      default: () => config.get("Popper.placement"),
      validator(value) {
        return Popper.placements.includes(value)
      },
    },
    visibleArrow: {
      type: Boolean,
      default: () => config.get("Popper.visibleArrow"),
    },
    trigger: {
      type: [String, Array],
      default: () => config.get("Popper.trigger"),
    },
    offset: {
      type: null,
      default: () => config.get("Popper.offset"),
    },
    delay: {
      type: Number,
      default: () => config.get("Popper.delay"),
    },
    lazyRender: {
      type: Boolean,
      default: () => config.get("Popper.lazyRender"),
    },
    appendToBody: {
      type: Boolean,
      default: () => config.get("Popper.appendToBody"),
    },
    removeOnDestroy: {
      type: Boolean,
      default: () => config.get("Popper.removeOnDestroy"),
    },
    trackRefWidth: {
      type: Boolean,
      default: () => config.get("Popper.trackRefWidth"),
    },
    transitionEvent: {
      type: Object,
      default: () => ({}),
    },
    popperOptions: {
      type: [Object, Function],
      default: () => ({}),
    },
    disabledOutside: {
      type: Boolean,
      default: false,
    },
    arrowClass: {
      type: [String, Array, Object],
      default: "popper-arrow",
    },
    loading: {
      type: Boolean,
      default: false,
    },
    popperClass: {
      type: [String, Array, Object],
    },
    popperStyle: {
      type: [Object, String],
      default: () => ({}),
    },
    popperWidth: {
      type: String,
    },
    onVisibleChange: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    const _this = this
    return {
      localVisible: this.visible,
      rendered: false,
      refWidth: 0,
      get referenceNode() {
        return getNodes(_this.target, _this.$vnode.context)[0]
      },
      get popperNode() {
        return _this.$refs.popper
      },
      childRefs: [],
    }
  },
  computed: {
    events() {
      return transformTrigger(this.trigger)
    },
    outsideOptions() {
      return {
        disabled: this.disabledOutside,
        handler: this.closeHandler,
        refs: this.outsideRefs,
        trigger: EVENTS.includes(this.trigger) ? this.trigger : "click",
        delay: this.trigger === "hover" && this.delay,
      }
    },
    outsideRefs() {
      return [this.referenceNode].concat(this.childRefs)
    },

    realPopperStyle() {
      const style = {
        ...this.popperStyle,
      }
      if (this.trackRefWidth) {
        style.width = this.refWidth
      }
      if (this.popperWidth) {
        style.width = this.popperWidth
      }
      return style
    },
  },
  watch: {
    loading() {
      this.update()
    },
    visible(value) {
      this.localVisible = value
    },
    localVisible: {
      immediate: true,
      handler(value) {
        if (value) {
          if (!this.rendered) {
            this.rendered = true
            this.$nextTick(() => {
              this.createPopper()
              this.updateVisible(value)
            })
          } else {
            this.$nextTick(() => {
              this.updatePopper()
              this.updateVisible(value)
            })
          }
        } else {
          if (this.lazyRender && this.rendered && this.removeOnDestroy) {
            this.$once("after-leave", () => {
              this.destroyPopper()
              this.rendered = false
              this.updateVisible(value)
            })
          } else {
            this.updatePopper()
            this.updateVisible(value)
          }
        }
      },
    },
  },
  mounted() {
    if (this.trackRefWidth && this.referenceNode) {
      addListener(this.referenceNode, this.resizeHandler)
      this.$nextTick(() => {
        this.resetReferenceWidth()
      })
    }
  },
  beforeDestroy() {
    if (this.trackRefWidth && this.referenceNode) {
      removeListener(this.referenceNode, this.resizeHandler)
    }

    if (this.popperNode && this.popperNode.parentNode) {
      this.popperNode.parentNode.removeChild(this.popperNode)
    }
  },
  destroyed() {
    this.destroyPopper(true)
  },
  methods: {
    updateVisible(value) {
      this.$emit("update:visible", value)
      this.$emit("visible-change", value)
      this.onVisibleChange(value)
    },
    update() {
      this.updatePopper()
    },
    resizeHandler() {
      this.resetReferenceWidth()
    },
    resetReferenceWidth() {
      if (this.referenceNode) {
        this.refWidth = this.referenceNode.getBoundingClientRect().width + "px"
      }
    },
    closeHandler() {
      this.localVisible = false
    },
    createPopper() {
      if (!this.referenceNode || !this.popperNode) {
        return
      }

      if (
        this.appendToBody &&
        (!this.popperNode.parentNode || this.popperNode.parentNode.tagName !== "BODY")
      ) {
        document.body.appendChild(this.popperNode)
      }

      const scrollParent = getScrollParent(this.referenceNode)
      const boundariesElement =
        scrollParent === document.body || scrollParent.scrollHeight === scrollParent.clientHeight
          ? "viewport"
          : "scrollParent"

      const popperOptions = {
        placement: this.placement,
        modifiers: {
          arrow: {
            enabled: this.visibleArrow,
            element: this.$refs.arrow,
          },
          offset: {
            offset: this.offset,
          },
          flip: {
            boundariesElement: boundariesElement,
          },
          preventOverflow: {
            padding: 0,
            boundariesElement: boundariesElement, // document.body,
            escapeWithReference: true,
          },
          computeStyle: {
            gpuAcceleration: false,
          },
        },
        onCreate: this.updateTransformOrigin,
        onUpdate: this.updateTransformOrigin,
      }

      this.popperInstance = new Popper(
        this.referenceNode,
        this.popperNode,
        merge(
          popperOptions,
          isFunction(this.popperOptions) ? this.popperOptions(Popper) : this.popperOptions
        )
      )

      this.appendChildRefsToParent(this.popperNode)
    },
    updatePopper() {
      if (!this.popperInstance) {
        return
      }
      if (this.localVisible) {
        if (this.popperInstance.reference === this.referenceNode) {
          this.popperInstance.enableEventListeners()
          this.popperInstance.update()
        } else {
          this.destroyPopper()
          this.createPopper()
        }
      } else {
        this.popperInstance.disableEventListeners()
      }
    },
    destroyPopper(force = true) {
      if (!this.popperInstance || (this.localVisible && !force)) {
        return
      }
      this.popperInstance.destroy()
      this.popperInstance = null

      this.removeChildRefsRefInParent(this.popperNode)
    },

    addRef(ref) {
      this.childRefs.push(ref)
      this.appendChildRefsToParent(ref)
    },
    removeRef(ref) {
      const index = this.childRefs.findIndex(item => item === ref)
      if (~index) {
        this.childRefs.splice(index, 1)
      }
      this.removeChildRefsRefInParent(ref)
    },

    appendChildRefsToParent(ref) {
      if (this.appendToBody && this.popperParent) {
        this.popperParent.addRef(ref)
      }
    },
    removeChildRefsRefInParent(ref) {
      if (this.appendToBody && this.popperParent) {
        this.popperParent.removeRef(ref)
      }
    },
    updateTransformOrigin(data) {
      const positionMap = {
        top: "bottom",
        bottom: "top",
        left: "right",
        right: "left",
      }
      const variationMap = {
        start: "0%",
        end: "100%",
      }

      const [position, variation] = data.placement.split("-")
      const p = positionMap[position]
      const v = variationMap[variation]

      data.instance.popper.style.transformOrigin =
        typeof this.transformOrigin === "string"
          ? this.transformOrigin
          : ["top", "bottom"].indexOf(p) > -1
            ? `${v} ${p}`
            : `${p} ${v}`
    },
  },
}
</script>
